import React, { useEffect, useState, useCallback } from 'react';
import { useAuth } from '../context/AuthContext';
import { Customer, Address, Integrator } from '../types';
import { customerApi, addressApi } from '../api/machines';
import { integratorApi } from '../api/integrators';

const MAX_LOGO_BYTES = 5 * 1024 * 1024;

/** Wspólna karta logo (klient i integrator) — identyczny wygląd i szerokości przycisków. */
const LogoCard: React.FC<{
  logoUrl: string | null;
  uploading: boolean;
  error: string;
  canEdit: boolean;
  readOnlyHint?: string;
  onUpload: (file: File) => void;
  onRemove: () => void;
}> = ({ logoUrl, uploading, error, canEdit, readOnlyHint, onUpload, onRemove }) => {
  const onFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    e.target.value = '';
    if (file) onUpload(file);
  };
  return (
    <div className="card" style={{ padding: '1.25rem', marginBottom: '1rem' }}>
      <div className="eyebrow" style={{ marginBottom: '0.75rem' }}>Logo firmy</div>
      {error && <div className="auth-error">{error}</div>}
      <div style={{ display: 'flex', alignItems: 'center', gap: '1rem', flexWrap: 'wrap' }}>
        <div style={{
          width: 120, height: 120,
          borderRadius: 8, overflow: 'hidden',
          border: '1px solid var(--color-border)',
          background: 'var(--color-surface-alt)',
          display: 'flex', alignItems: 'center', justifyContent: 'center',
        }}>
          {logoUrl ? (
            <img src={logoUrl} alt="Logo firmy"
              style={{ width: '100%', height: '100%', objectFit: 'contain' }} />
          ) : (
            <span style={{ color: 'var(--color-text-muted)', fontSize: '0.875rem' }}>brak logo</span>
          )}
        </div>
        {/* alignItems: flex-start + stała szerokość — bez tego flex rozciąga przyciski
            do szerokości najdłuższego elementu kolumny (tekstu z podpowiedzią). */}
        <div style={{ display: 'flex', flexDirection: 'column', gap: '0.5rem', alignItems: 'flex-start' }}>
          {canEdit ? (
            <>
              <label className="btn btn-primary btn-sm" style={{ cursor: 'pointer', width: 160, textAlign: 'center' }}>
                {uploading ? 'Wysyłanie...' : (logoUrl ? 'Zmień logo' : 'Dodaj logo')}
                <input type="file" accept=".png,.jpg,.jpeg,.gif,.webp,.bmp" hidden disabled={uploading} onChange={onFileChange} />
              </label>
              {logoUrl && (
                <button className="btn btn-danger btn-sm" style={{ width: 160 }} disabled={uploading} onClick={onRemove}>
                  Usuń logo
                </button>
              )}
              <span className="item-meta">PNG / JPG / WEBP, max 5 MB. Logo widać w sidebarze przy nazwie firmy.</span>
            </>
          ) : (
            <span className="item-meta">{readOnlyHint || 'Nie masz uprawnień do zmiany logo.'}</span>
          )}
        </div>
      </div>
    </div>
  );
};

/** Konto firmy klienta (CUSTOMER): dane firmy + logo. */
const CustomerCompanyAccount: React.FC = () => {
  const { refreshUser } = useAuth();
  const [customer, setCustomer] = useState<Customer | null>(null);
  const [address, setAddress] = useState<Address | null>(null);
  const [loading, setLoading] = useState(true);
  const [loadError, setLoadError] = useState('');
  const [uploading, setUploading] = useState(false);
  const [actionError, setActionError] = useState('');

  const load = useCallback(async () => {
    setLoading(true);
    setLoadError('');
    try {
      const { data } = await customerApi.getMe();
      setCustomer(data);
      if (data.addressId) {
        const addrRes = await addressApi.getById(data.addressId);
        setAddress(addrRes.data);
      }
    } catch (err: any) {
      const status = err?.response?.status;
      if (status === 403) setLoadError('Twoje konto nie jest powiązane z żadnym klientem.');
      else if (status === 404) setLoadError('Nie znaleziono profilu klienta.');
      else setLoadError('Nie udało się załadować profilu.');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  // Sidebar czyta logo z AuthContext — po zmianie pobieramy świeży stan konta z backendu.
  const syncSidebar = async () => { try { await refreshUser(); } catch { /* best-effort */ } };

  const validate = (file: File): string | null => {
    if (!file.type.startsWith('image/')) return 'Logo musi być obrazem';
    if (file.size > MAX_LOGO_BYTES) return 'Logo przekracza 5 MB';
    return null;
  };

  const upload = async (file: File) => {
    setActionError('');
    const invalid = validate(file);
    if (invalid) { setActionError(invalid); return; }
    setUploading(true);
    try {
      const { data } = await customerApi.uploadOwnLogo(file);
      setCustomer(data);
      await syncSidebar();
    } catch (err: any) {
      setActionError(err?.response?.data?.message || 'Nie udało się wgrać logo');
    } finally {
      setUploading(false);
    }
  };

  const remove = async () => {
    if (!window.confirm('Usunąć logo?')) return;
    setActionError('');
    setUploading(true);
    try {
      const { data } = await customerApi.deleteOwnLogo();
      setCustomer(data);
      await syncSidebar();
    } catch (err: any) {
      setActionError(err?.response?.data?.message || 'Nie udało się usunąć logo');
    } finally {
      setUploading(false);
    }
  };

  if (loading) return <div className="empty-state">Ładowanie...</div>;
  if (loadError) return <div className="auth-error">{loadError}</div>;
  if (!customer) return null;

  return (
    <div>
      <h1 className="page-title">Konto firmy</h1>
      <LogoCard logoUrl={customer.logoUrl} uploading={uploading} error={actionError}
        canEdit onUpload={upload} onRemove={remove} />
      <div className="card" style={{ padding: '1.25rem' }}>
        <div className="eyebrow" style={{ marginBottom: '0.75rem' }}>Dane firmy</div>
        <div className="info-grid">
          {customer.companyName && (
            <div className="info-item">
              <span className="info-label">Nazwa firmy</span>
              <span className="info-value">{customer.companyName}</span>
            </div>
          )}
          <div className="info-item">
            <span className="info-label">NIP</span>
            <span className="info-value">{customer.nip}</span>
          </div>
          {address && (
            <div className="info-item">
              <span className="info-label">Adres</span>
              <span className="info-value">
                ul. {address.street} {address.buildingNumber}
                {address.apartmentNumber && `/${address.apartmentNumber}`}
                {', '}{address.postalCode} {address.city}
                {address.country && `, ${address.country}`}
              </span>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

/** Konto firmy integratora (staff): dane firmy + logo; logo zmienia tylko admin integratora. */
const IntegratorCompanyAccount: React.FC<{ canEdit: boolean }> = ({ canEdit }) => {
  const { refreshUser } = useAuth();
  const [integrator, setIntegrator] = useState<Integrator | null>(null);
  const [loading, setLoading] = useState(true);
  const [loadError, setLoadError] = useState('');
  const [uploading, setUploading] = useState(false);
  const [actionError, setActionError] = useState('');

  useEffect(() => {
    integratorApi.me()
      .then(({ data }) => setIntegrator(data))
      .catch((err: any) => {
        setLoadError(err?.response?.status === 404
          ? 'Twoje konto nie jest przypisane do integratora.'
          : 'Nie udało się załadować danych firmy.');
      })
      .finally(() => setLoading(false));
  }, []);

  const syncSidebar = async () => { try { await refreshUser(); } catch { /* best-effort */ } };

  const upload = async (file: File) => {
    setActionError('');
    if (!file.type.startsWith('image/')) { setActionError('Logo musi być obrazem'); return; }
    if (file.size > MAX_LOGO_BYTES) { setActionError('Logo przekracza 5 MB'); return; }
    setUploading(true);
    try {
      const { data } = await integratorApi.uploadMyLogo(file);
      setIntegrator(data);
      await syncSidebar();
    } catch (err: any) {
      setActionError(err?.response?.data?.detail || err?.response?.data?.message || 'Nie udało się wgrać logo');
    } finally {
      setUploading(false);
    }
  };

  const remove = async () => {
    if (!window.confirm('Usunąć logo?')) return;
    setActionError('');
    setUploading(true);
    try {
      const { data } = await integratorApi.deleteMyLogo();
      setIntegrator(data);
      await syncSidebar();
    } catch (err: any) {
      setActionError(err?.response?.data?.detail || err?.response?.data?.message || 'Nie udało się usunąć logo');
    } finally {
      setUploading(false);
    }
  };

  if (loading) return <div className="empty-state">Ładowanie...</div>;
  if (loadError) return <div className="empty-state">{loadError}</div>;
  if (!integrator) return null;

  const addressLine = integrator.billingStreet
    ? `ul. ${integrator.billingStreet} ${integrator.billingBuildingNo || ''}`
        + `${integrator.billingApartmentNo ? `/${integrator.billingApartmentNo}` : ''}`
        + `, ${integrator.billingPostCode || ''} ${integrator.billingCity || ''}`
    : null;

  return (
    <div>
      <h1 className="page-title">Konto firmy</h1>
      <LogoCard logoUrl={integrator.logoUrl} uploading={uploading} error={actionError}
        canEdit={canEdit} readOnlyHint="Logo firmy może zmienić admin integratora."
        onUpload={upload} onRemove={remove} />
      <div className="card" style={{ padding: '1.25rem' }}>
        <div className="eyebrow" style={{ marginBottom: '0.75rem' }}>Dane firmy</div>
        <div className="info-grid">
          <div className="info-item">
            <span className="info-label">Nazwa firmy</span>
            <span className="info-value">{integrator.name}</span>
          </div>
          <div className="info-item">
            <span className="info-label">NIP</span>
            <span className="info-value">{integrator.nip || '—'}</span>
          </div>
          {addressLine && (
            <div className="info-item">
              <span className="info-label">Adres</span>
              <span className="info-value">{addressLine}</span>
            </div>
          )}
          {integrator.billingEmail && (
            <div className="info-item">
              <span className="info-label">E-mail rozliczeniowy</span>
              <span className="info-value">{integrator.billingEmail}</span>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

const CompanyAccount: React.FC = () => {
  const { user } = useAuth();
  const roles = user?.roles || [];
  const isCustomer = roles.includes('CUSTOMER');
  const isStaff = roles.includes('ADMIN') || roles.includes('EMPLOYEE_ADMIN') || roles.includes('EMPLOYEE');

  if (isCustomer) return <CustomerCompanyAccount />;
  if (isStaff) {
    return <IntegratorCompanyAccount canEdit={roles.includes('EMPLOYEE_ADMIN') || roles.includes('ADMIN')} />;
  }
  return <div className="empty-state">Ta strona jest dostępna po zalogowaniu.</div>;
};

export default CompanyAccount;
